import { useState, useEffect } from 'react';
import { createHttpClient } from '../libs/HttpRequest';
import useToken from './useToken';

interface ApiState<T> {
    data: T | null;
    loading: boolean;
    error: string | null;
}

export const useApi = <T,>(url: string, method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET', immediate: boolean = true) => {
    const [getToken, tokenState] = useToken();
    const [state, setState] = useState<ApiState<T>>({
        data: null,
        loading: false,
        error: null
    });

    const makeRequest = async (payload?: any) => {
        if (!tokenState.isAuthenticated) {
            setState(prev => ({ ...prev, error: 'User not authenticated', loading: false }));
            return;
        }

        setState(prev => ({ ...prev, loading: true, error: null }));

        try {
            const client = createHttpClient(getToken);
            const response = await client.request<T>({
                method,
                url,
                data: payload,
                headers: {
                    'Accept': 'application/json'
                }
            });

            setState({
                data: response.data,
                loading: false,
                error: null
            });

            return response.data;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Request failed';
            setState({
                data: null,
                loading: false,
                error: errorMessage
            });
            throw error;
        }
    };

    useEffect(() => {
        if (immediate && tokenState.isAuthenticated && !tokenState.isLoading) {
            makeRequest();
        }
    }, [tokenState.isAuthenticated, tokenState.isLoading, immediate]);

    return {
        ...state,
        makeRequest,
        isReady: tokenState.isAuthenticated && !tokenState.isLoading
    };
};

export default useApi;
